import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Layout,
  message,
  Select
} from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import AgentConfigService from '../services/AgentConfigService';
import sessionStorageManager from '../services/sessionStorageManager';
import SessionService from '../services/SessionService';
import EnvVersionLabel from './Envversionlabel';


const { Title } = Typography;
const { Content } = Layout;

/**
 * Login page component for user authentication
 * 
 * @param {Object} props
 * @param {Function} props.onLogin - Callback when login is successful
 *   Signature: onLogin(username, sessionId, userRole, features, defaultTab)
 */
const LoginPage = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);

  const [clientIds, setClientIds] = useState([]);
  const [selectedClient, setSelectedClient] = useState('default');

  // Fetch client IDs from public folder on mount
  React.useEffect(() => {
    fetch('/clients/client_ids.txt')
      .then(r => r.ok ? r.text() : '')
      .then(text => {
        const ids = text.split('\n').map(s => s.trim()).filter(Boolean);
        setClientIds(ids);
      })
      .catch(() => setClientIds([]));
  }, []);  


  // Handle form submission
  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      console.log('Starting fresh login process...');
      
      // STEP 1: Clear any existing session data before logging in
      await clearExistingSessionData();
      
      // STEP 2: Get the main API URL
      const mainApiUrl = await AgentConfigService.getMainApiUrl();

      console.log('main API URL   ', mainApiUrl)
      
      // STEP 3: Call the login API
      const response = await fetch(`${mainApiUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: values.username,
          password: values.password,
          client_id: selectedClient
        }),
        credentials: 'include' // Include cookies in the request
      });
      
      if (!response.ok) {
        throw new Error(`Login failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Login response data:', data);
      
      if (data.success) {
        console.log('Login successful, calling onLogin callback');
        console.log(`Default tab for user: ${data.default_tab}`);
        console.log(`Tab order: ${JSON.stringify(data.tab_order)}`);  // NEW: Log tab order
        
        // Call the onLogin callback with username, session ID, role, features, default_tab, AND tab_order
        onLogin(
          data.username, 
          data.session_id, 
          data.user_role, 
          data.features,
          data.default_tab,
          data.tab_order  // NEW: Pass tab order
        );
      } else {
        throw new Error('Login failed');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  


  // Updated clearExistingSessionData using SessionService
  const clearExistingSessionData = async () => {
    try {
      console.log('Clearing existing session data...');
      
      // Get any existing session ID
      const existingSessionId = localStorage.getItem('sessionId');
      
      // If there's an existing session, try to logout from backend first
      if (existingSessionId) {
        console.log('Found existing session, attempting backend logout...');
        try {
          const mainApiUrl = await AgentConfigService.getMainApiUrl();
          
          // Call the logout endpoint (fire and forget)
          fetch(`${mainApiUrl}/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${existingSessionId}`
            },
            credentials: 'include'
          }).catch(err => {
            console.log('Logout API error (non-blocking):', err);
          });
        } catch (error) {
          console.warn('Error calling logout API:', error);
        }
      }
      
      // Use SessionService to clear all storage data
      SessionService.clearAllStorageData();
      
      // Also clear the config cache to ensure fresh data
      AgentConfigService.clearConfigCache();
      
      console.log('Session cleanup completed');
      
    } catch (error) {
      console.error('Error during session cleanup:', error);
      // Don't fail the login process if cleanup fails
    }
  };



  /**
   * Clear any existing session data to ensure a fresh start
   */
  const clearExistingSessionData_old = async () => {
    try {
      console.log('Clearing existing session data...');
      
      // Get any existing session ID from localStorage
      const existingSessionId = localStorage.getItem('sessionId');
      
      // If there's an existing session, try to logout from backend first
      if (existingSessionId) {
        console.log('Found existing session, attempting to logout from backend...');
        try {
          const mainApiUrl = await AgentConfigService.getMainApiUrl();
          
          // Call the logout endpoint (fire and forget)
          fetch(`${mainApiUrl}/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${existingSessionId}`
            },
            credentials: 'include'
          }).catch(err => {
            console.log('Logout API error (non-blocking):', err);
          });
        } catch (error) {
          console.warn('Error calling logout API:', error);
        }
      }
      
      // Clear all localStorage data managed by sessionStorageManager
      const clearedCount = sessionStorageManager.clearAllSessionStorageManagerData();
      console.log(`Cleared ${clearedCount} sessionStorageManager items`);
      
      // Clear standard session items that might not be managed by sessionStorageManager
      const standardSessionKeys = [
        'sessionId',
        'username', 
        'userRole',
        'session_id'
      ];
      
      standardSessionKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`Removed standard session key: ${key}`);
        }
      });
      
      // Clear any legacy agent-specific data
      const legacyKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('last_workflow_') || 
        key.startsWith('agent_key_') ||
        key.startsWith('component_map_') ||
        key.startsWith('agent_state_')
      );
      
      legacyKeys.forEach(key => {
        localStorage.removeItem(key);
        console.log(`Removed legacy key: ${key}`);
      });
      
      // Clear all cookies
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      });
      
      // Clear sessionStorage as well
      try {
        sessionStorage.clear();
        console.log('Cleared sessionStorage');
      } catch (e) {
        console.warn('Error clearing sessionStorage:', e);
      }
      
      console.log('Session cleanup completed');
      
    } catch (error) {
      console.error('Error during session cleanup:', error);
      // Don't fail the login process if cleanup fails
    }
  };
  
  return (
    <Layout style={{ minHeight: '100vh', 
      //background: 'linear-gradient(to bottom, #2196F3 0%, #2196F3 33%, #7e42df 90%, #8e5ce5 100%)' 
      //background: 'linear-gradient(to bottom, #f0f2f5 0%, #e8eaed 33%, #dfe2e6 90%, #d6d9dd 100%)'
      //background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #e9ecef 90%, #dee2e6 100%)'
      //background: 'linear-gradient(to bottom, #f0f2f5 0%, #dee2e6 33%, #ced4da 90%, #adb5bd 100%)'
      //background: 'linear-gradient(to bottom, #adb5bd 0%, #ced4da 33%, #dee2e6 90%, #f0f2f5 100%)'
      background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)'
      //background: '#f0f2f5' 
      }}>
      <Content style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' ,
        background: 'transparent'
      }}>
        <Card 
          style={{ 
            width: 520, 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px'
          }}
        >
          <div style={{ marginBottom: '24px' }}>
            <img 
              src="/resources/logos/onaxis-logo-light.png"
              alt="Company Logo"
              style={{ 
                height: '40px',
                width: 'auto',
                maxWidth: '100%',
                display: 'block',      // Make it a block element
                margin: '0 auto'       // Center horizontally
              }}
            />
          </div>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={2} style={{ color: '#162349' }}>
              Chiron - Retrospective Risk Adjustment
            </Title>
            <Title level={4} style={{ color: '#888', fontWeight: 'normal' }}>
              Login to your account
            </Title>
          </div>
          
          <Form
            name="login"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
          >

            {clientIds.length > 0 && (
              <Form.Item label="Select Installation">
                <Select
                  value={selectedClient}
                  onChange={setSelectedClient}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="default">Default</Select.Option>
                  {clientIds.map(id => (
                    <Select.Option key={id} value={id}>{id}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Please enter your username' }]}
            >
              <Input 
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
                placeholder="Username" 
                size="large"
              />
            </Form.Item>
            
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password 
                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                placeholder="Password" 
                size="large"
              />
            </Form.Item>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                style={{ 
                  width: '100%', 
                  height: '40px', 
                  background: '#162349',
                  borderColor: '#5000c4'
                }}
                loading={loading}
              >
                Log in
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Content>
      <EnvVersionLabel
        style={{ 
          position: 'fixed', 
          bottom: 10, 
          left: 10, 
          color: '#4c4a4aff', 
          fontSize: '10px' ,
          fontWeight: "Bold"
        }} 
      />
    </Layout>
  );
};

export default LoginPage;
